import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import { enqueue, isNetworkError } from "../lib/outbox.js";
import { dollarsToCents, todayISO } from "../lib/money.js";
import { Field, Segmented, SelectWithOther, SaveButton, inputCls } from "./ui.jsx";
import SnapReceiptButton from "./ReceiptCapture.jsx";

export const PAYMENT_METHODS = ["Card", "Bank transfer", "Cash", "Check", "Stripe", "PayPal"];

const TYPE_OPTIONS = [
  { value: "expense", label: "Expense" },
  { value: "income", label: "Income" },
  { value: "owner", label: "Owner" },
];

// Shared by Add, Edit, and receipt Review. `initial` = existing transaction
// (edit); `prefill` = OCR-suggested draft values (new entry, editable);
// `receiptId` attaches the receipt to the created transaction.
export default function TransactionForm({ initial, prefill, receiptId, onSaved, submitLabel = "Save" }) {
  const seed = initial || prefill || null;
  const [accounts, setAccounts] = useState([]);
  const [customOptions, setCustomOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState(seed?.type?.startsWith("owner") ? "owner" : seed?.type || "expense");
  const [ownerKind, setOwnerKind] = useState(
    seed?.type === "owner_contribution" ? "owner_contribution" : "owner_draw",
  );
  const [amount, setAmount] = useState(seed?.amount_cents ? (seed.amount_cents / 100).toFixed(2) : "");
  const [date, setDate] = useState(seed?.date || todayISO());
  const [accountId, setAccountId] = useState(seed?.account_id || "");
  const [incomeType, setIncomeType] = useState(seed?.income_type || "service");
  const [vendor, setVendor] = useState(seed?.vendor || "");
  const [paymentMethod, setPaymentMethod] = useState(seed?.payment_method || "");
  const [pct, setPct] = useState(seed?.business_use_pct ?? 100);
  const [memo, setMemo] = useState(seed?.memo || "");
  // Receipt captured from within the form (Snap-to-auto-fill)
  const [snapped, setSnapped] = useState(null); // { id, lowConfidence, failed }
  // Money already in the books that looks like what's being entered.
  const [dupes, setDupes] = useState([]);
  const [attached, setAttached] = useState("");

  function applyOcr({ id, ocr, ocrError }) {
    if (ocr) {
      // Fill only fields the user hasn't touched; the photo shouldn't clobber typing.
      if (!amount && ocr.total_cents) setAmount((ocr.total_cents / 100).toFixed(2));
      if (!vendor && ocr.merchant) setVendor(ocr.merchant);
      if (!memo && ocr.summary) setMemo(ocr.summary);
      if (!accountId && ocr.category_id && ocr.category_id !== "unknown") setAccountId(ocr.category_id);
      if (!paymentMethod && ocr.payment_method_hint) setPaymentMethod(ocr.payment_method_hint);
      if (/^\d{4}-\d{2}-\d{2}$/.test(ocr.date)) setDate(ocr.date);
      setType("expense");
    }
    setSnapped({ id, lowConfidence: ocr ? (ocr.confidence ?? 0) < 0.7 : false, failed: !!ocrError });
  }

  useEffect(() => {
    api("/api/accounts").then((d) => {
      setAccounts(d.accounts || []);
      setCustomOptions((d.custom_options || []).filter((o) => o.field === "payment_method").map((o) => o.value));
    });
    api("/api/vendors").then((d) => setVendors(d.vendors || []));
  }, []);

  // A receipt photographed for a charge the bank already delivered is the
  // classic double-entry. Look for its twin as soon as the amount and date
  // are known — a warning after saving is a warning too late.
  const realTypeForCheck = type === "owner" ? null : type;
  useEffect(() => {
    const cents = dollarsToCents(amount);
    if (!cents || !realTypeForCheck || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return setDupes([]);
    const t = setTimeout(() => {
      const q = new URLSearchParams({ check: "1", date, amount_cents: String(cents), type: realTypeForCheck });
      if (initial?.id) q.set("exclude", initial.id);
      api(`/api/money/duplicates?${q}`)
        .then((d) => setDupes(d.candidates || []))
        .catch(() => setDupes([]));
    }, 400);
    return () => clearTimeout(t);
  }, [amount, date, realTypeForCheck, initial?.id]);

  async function attachToExisting(txnId) {
    const receipt = receiptId || snapped?.id;
    if (!receipt) return;
    try {
      await api("/api/money/duplicates", { method: "POST", body: { action: "attach", receipt_id: receipt, transaction_id: txnId } });
      setAttached(txnId);
      onSaved?.({ queued: false, attached: true });
    } catch (err) {
      setError(err.message || "Could not attach the receipt");
    }
  }

  const categoryChoices = useMemo(() => {
    const t = type === "owner" ? null : type;
    return accounts.filter((a) => a.type === t);
  }, [accounts, type]);

  // Default the category sensibly when switching type.
  useEffect(() => {
    if (type === "owner") return;
    if (!categoryChoices.some((a) => a.id === accountId)) setAccountId("");
  }, [type, categoryChoices, accountId]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    const cents = dollarsToCents(amount);
    if (!cents) return setError("Enter a valid amount.");
    const realType = type === "owner" ? ownerKind : type;
    if (type !== "owner" && !accountId) return setError("Pick a category.");

    setBusy(true);
    try {
      const body = {
        type: realType,
        amount_cents: cents,
        date,
        account_id: accountId,
        income_type: type === "income" ? incomeType : undefined,
        vendor,
        payment_method: paymentMethod,
        business_use_pct: pct,
        memo,
      };
      const method = initial?.id ? "PUT" : "POST";
      const path = initial?.id ? `/api/transactions/${initial.id}` : "/api/transactions";
      if (!initial?.id) body.client_id = crypto.randomUUID().replace(/-/g, "");
      const attachReceipt = receiptId || snapped?.id;
      if (attachReceipt && !initial?.id) body.receipt_id = attachReceipt;
      try {
        await api(path, { method, body });
        onSaved?.({ queued: false });
      } catch (err) {
        if (isNetworkError(err)) {
          // No signal — queue it. client_id makes the eventual replay safe.
          await enqueue(method, path, body);
          onSaved?.({ queued: true });
        } else {
          throw err;
        }
      }
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Snap-to-auto-fill: only for brand-new entries not already tied to a receipt */}
      {!initial?.id && !receiptId && !snapped && (
        <SnapReceiptButton
          label="📷 Snap receipt to auto-fill"
          className="w-full rounded-xl border-2 border-dashed border-navy-600/40 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700 disabled:opacity-60"
          onCaptured={applyOcr}
        />
      )}
      {snapped && (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-2">
          <img src={`/api/receipts/${snapped.id}/image?size=thumb`} alt="Receipt" className="h-14 w-11 rounded-lg object-cover" />
          <p className="text-xs text-slate-600">
            {snapped.failed
              ? "Couldn't read the photo — fill in the details; the image stays attached."
              : snapped.lowConfidence
                ? "⚠️ Low-confidence read — double-check amount and date."
                : "Receipt read ✓ — review the fields below, then save."}
          </p>
        </div>
      )}

      <Segmented options={TYPE_OPTIONS} value={type} onChange={setType} />

      {type === "owner" && (
        <Segmented
          options={[
            { value: "owner_draw", label: "Draw (money out)" },
            { value: "owner_contribution", label: "Contribution (money in)" },
          ]}
          value={ownerKind}
          onChange={setOwnerKind}
        />
      )}

      <Field label="Amount">
        <div className="relative">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xl font-semibold text-slate-400">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className={`${inputCls} pl-9 text-2xl font-semibold`}
            autoFocus={!initial}
          />
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label={type === "income" ? "Client" : "Vendor"}>
          <input
            type="text"
            list="vendor-suggestions"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            placeholder={type === "income" ? "Who paid you?" : "Who did you pay?"}
            className={inputCls}
          />
          <datalist id="vendor-suggestions">
            {vendors.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </Field>
      </div>

      {type !== "owner" && (
        <Field label="Category" hint={accounts.find((a) => a.id === accountId)?.schedule_c_line}>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputCls}>
            <option value="">Select a category…</option>
            {categoryChoices.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {type === "income" && (
        <Field label="Income type">
          <Segmented
            options={[
              { value: "service", label: "Service" },
              { value: "product", label: "Product" },
            ]}
            value={incomeType}
            onChange={setIncomeType}
          />
        </Field>
      )}

      <Field label="Payment method">
        <SelectWithOther
          value={paymentMethod}
          onChange={setPaymentMethod}
          options={[...PAYMENT_METHODS, ...customOptions.filter((c) => !PAYMENT_METHODS.includes(c))]}
        />
      </Field>

      {type === "expense" && (
        <Field label="Business use %" hint="Portion of this expense that's business (e.g. shared internet).">
          <div className="flex items-center gap-2">
            {[100, 75, 50].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setPct(v)}
                className={`rounded-lg px-3 py-2 text-sm font-semibold ${
                  pct === v ? "bg-navy-700 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {v}%
              </button>
            ))}
            <input
              type="number"
              min="0"
              max="100"
              value={pct}
              onChange={(e) => setPct(Number(e.target.value))}
              className={`${inputCls} w-24 py-2`}
            />
          </div>
        </Field>
      )}

      <Field label="Memo (optional)">
        <input
          type="text"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="What was this for?"
          className={inputCls}
        />
      </Field>

      {dupes.length > 0 && !attached && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">⚑ This may already be in your books</p>
          <ul className="mt-2 space-y-2">
            {dupes.map((d) => (
              <li key={d.id} className="rounded-lg bg-white/70 p-2">
                <p className="text-sm text-slate-800">
                  {d.vendor || "—"} · {d.date} · {d.account_name || ""}
                </p>
                <p className="text-xs text-slate-500">
                  {d.source === "import"
                    ? "already imported from your bank statement"
                    : d.source === "ocr"
                      ? "already recorded from a receipt photo"
                      : `already recorded (${d.source})`}
                  {d.receipt_id ? " · has a receipt" : ""}
                </p>
                {(receiptId || snapped?.id) && !initial?.id && (
                  <button
                    type="button"
                    onClick={() => attachToExisting(d.id)}
                    className="mt-2 w-full rounded-lg bg-amber-600 py-2 text-xs font-semibold text-white"
                  >
                    Same purchase — attach this photo to it
                  </button>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-800">
            If this really is a separate charge, carry on and save — nothing is blocked.
          </p>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton busy={busy}>{submitLabel}</SaveButton>
    </form>
  );
}
